import React, { useState } from 'react'
import { addPromoCodeToBasket } from '@lib/spektrix'
import { TagIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import BasketUpdateEvent from '@lib/basket-update-event'

const PromoCodeSection = ({ basketData, onUpdate }) => {
  const [showInput, setShowInput] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const existingPromoCode = basketData?.promoCode

  const handleAddPromoCode = async (e) => {
    e.preventDefault()
    if (!promoCode.trim()) return

    setIsSubmitting(true)
    setError(null)
    
    const codeToAdd = promoCode.trim()

    // Optimistically update the basket data with the new promo code
    if (onUpdate && basketData) {
      const updatedBasketData = { ...basketData, promoCode: codeToAdd }
      // SWR mutate with optimistic update
      onUpdate(updatedBasketData, false)
    }

    try {
      const response = await addPromoCodeToBasket(codeToAdd)
      
      if (response.ok) {
        // Trigger basket update event
        window.dispatchEvent(new Event(BasketUpdateEvent))
        
        // Revalidate to get the updated basket from server
        if (onUpdate) {
          onUpdate()
        }
        
        // Reset form
        setPromoCode('')
        setShowInput(false)
      } else {
        // If adding fails, revert the optimistic update
        if (onUpdate) {
          onUpdate(basketData, false)
        }
        
        // Handle different error status codes
        if (response.status === 404) {
          setError(`The code '${codeToAdd}' is not valid`)
        } else {
          // Try to parse error message from response
          try {
            const data = await response.json()
            setError(data.message || 'Invalid promo code')
          } catch {
            setError('Invalid promo code')
          }
        }
      }
    } catch (error) {
      console.error('Error adding promo code:', error)
      // If adding fails, revert the optimistic update
      if (onUpdate) {
        onUpdate(basketData, false)
      }
      setError('Failed to add promo code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemovePromoCode = async () => {
    if (!window.confirm(`Are you sure you want to remove the promo code '${existingPromoCode}'?`)) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Optimistically update the basket data locally
    if (onUpdate && basketData) {
      // Create a copy of the basket data without the promo code
      const updatedBasketData = { ...basketData, promoCode: null }
      // SWR mutate with optimistic update
      onUpdate(updatedBasketData, false)
    }

    try {
      // To remove a promo code, we send an empty string
      const response = await addPromoCodeToBasket('')
      
      if (response.ok) {
        // Trigger basket update event
        window.dispatchEvent(new Event(BasketUpdateEvent))
        
        // Revalidate to get the updated basket from server
        if (onUpdate) {
          onUpdate()
        }
      } else {
        // If removal fails, revert the optimistic update
        if (onUpdate) {
          onUpdate()
        }
        setError('Failed to remove promo code. Please try again.')
      }
    } catch (error) {
      console.error('Error removing promo code:', error)
      // If removal fails, revert the optimistic update
      if (onUpdate) {
        onUpdate()
      }
      setError('Failed to remove promo code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
          <TagIcon className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
          Promo Code
        </h3>
      </div>

      {existingPromoCode ? (
        // Show existing promo code
        <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <span className="font-medium text-green-800 dark:text-green-200">
                Code Applied: {existingPromoCode}
              </span>
              {basketData?.offers && basketData.offers.length > 0 && (
                <p className="text-sm text-green-700 dark:text-green-300 mt-0.5">
                  Your promo code has been applied to eligible items
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleRemovePromoCode}
            disabled={isSubmitting}
            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
            title="Remove promo code"
          >
            {isSubmitting ? (
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
            ) : (
              <XMarkIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      ) : (
        // Show input or button to add promo code
        <>
          {!showInput ? (
            <button
              onClick={() => setShowInput(true)}
              className="w-full text-left p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 transition-colors group"
            >
              <span className="text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                + Add promo code
              </span>
            </button>
          ) : (
            <form onSubmit={handleAddPromoCode}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value)
                    setError(null)
                  }}
                  placeholder="Enter promo code"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  disabled={isSubmitting}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={isSubmitting || !promoCode.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Adding...' : 'Apply'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowInput(false)
                    setPromoCode('')
                    setError(null)
                  }}
                  className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              </div>
              {error && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              )}
            </form>
          )}
        </>
      )}
    </div>
  )
}

export default PromoCodeSection